import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Modal from '@components/common/Modal/Modal'
import ChecklistReviewItem from './ChecklistReviewItem'
import projectApprovalService from '@services/projectApprovalService'
import './PMReviewModal.css'

/**
 * PMReviewModal Component
 *
 * Modal for Project Manager to review and approve/excuse checklist items
 *
 * @param {boolean} isOpen - Modal open state
 * @param {Function} onClose - Close modal callback
 * @param {number} projectId - Project ID
 * @param {Array} checklists - Array of checklist items
 * @param {Function} onSuccess - Success callback after review submission
 */
const PMReviewModal = ({ isOpen, onClose, projectId, checklists, onSuccess }) => {
  const [decisions, setDecisions] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Reset decisions when modal opens
  useEffect(() => {
    if (isOpen) {
      setDecisions({})
      setError(null)
    }
  }, [isOpen])

  const handleAcceptAll = () => {
    const allAccepted = checklists.reduce((acc, item) => {
      acc[item.id] = { action: 'accept' }
      return acc
    }, {})
    setDecisions(allAccepted)
  }

  const handleItemDecision = (checklistId, action, reason = '') => {
    setDecisions((prev) => ({
      ...prev,
      [checklistId]: { action, reason },
    }))
  }

  const handleSubmit = async () => {
    // Validate all items are reviewed
    if (Object.keys(decisions).length !== checklists.length) {
      setError('Please review all checklist items before submitting')
      return
    }

    // Validate excused items have reasons
    const hasInvalidExcuses = Object.entries(decisions).some(
      ([_, decision]) => decision.action === 'excuse' && !decision.reason?.trim()
    )

    if (hasInvalidExcuses) {
      setError('All excused items must have a reason')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      // Convert decisions to API format
      const apiDecisions = Object.entries(decisions).map(([checklistId, decision]) => ({
        checklist_id: parseInt(checklistId),
        action: decision.action,
        ...(decision.action === 'excuse' && { reason: decision.reason }),
      }))

      const response = await projectApprovalService.pmReview(projectId, apiDecisions)

      // Success
      if (onSuccess) {
        onSuccess(response)
      }
      onClose()
    } catch (err) {
      // Extract error message from different possible locations in the response
      const errorMessage =
        err.response?.data?.messages?.error ||
        err.response?.data?.message ||
        err.message ||
        'Failed to submit review'
      setError(errorMessage)
      console.error('PM Review submission error:', err.response?.data || err)
    } finally {
      setSubmitting(false)
    }
  }

  const summary = {
    total: checklists.length,
    accepted: Object.values(decisions).filter((d) => d.action === 'accept').length,
    excused: Object.values(decisions).filter((d) => d.action === 'excuse').length,
    pending: checklists.length - Object.keys(decisions).length,
  }

  const allReviewed = Object.keys(decisions).length === checklists.length

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnBackdrop={false}>
      <div className="pm-review-modal">
        <div className="pm-review-modal__header">
          <h2>Review Project Checklists</h2>
          <p className="pm-review-modal__subtitle">
            Review each checklist item and either accept or excuse with a reason
          </p>
        </div>

        <div className="pm-review-modal__actions">
          <button className="btn btn--success" onClick={handleAcceptAll} disabled={submitting}>
            ✓ Accept All ({summary.total})
          </button>

          <div className="pm-review-modal__summary">
            <span className="pm-review-modal__summary-item pm-review-modal__summary-item--accepted">
              Accepted: {summary.accepted}
            </span>
            <span className="pm-review-modal__summary-item pm-review-modal__summary-item--excused">
              Excused: {summary.excused}
            </span>
            <span className="pm-review-modal__summary-item pm-review-modal__summary-item--pending">
              Pending: {summary.pending}
            </span>
          </div>
        </div>

        {error && (
          <div className="pm-review-modal__error">
            <span className="pm-review-modal__error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <div className="pm-review-modal__items">
          {checklists.length === 0 ? (
            <div className="pm-review-modal__empty">No checklist items to review</div>
          ) : (
            checklists.map((item, index) => (
              <ChecklistReviewItem
                key={item.id}
                item={item}
                index={index}
                decision={decisions[item.id]}
                onAccept={() => handleItemDecision(item.id, 'accept')}
                onExcuse={(reason) => handleItemDecision(item.id, 'excuse', reason)}
                disabled={submitting}
              />
            ))
          )}
        </div>

        <div className="pm-review-modal__footer">
          <button className="btn btn--secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={!allReviewed || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </div>
      </div>
    </Modal>
  )
}

PMReviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.number.isRequired,
  checklists: PropTypes.array.isRequired,
  onSuccess: PropTypes.func,
}

export default PMReviewModal
